import { useEffect, useState } from 'react';
import {
  BODY_TYPE_LABEL,
  CARGO_SORTS,
  CARGO_SORT_LABEL,
  ENTITY_TYPE_LABEL,
  deliveryEta,
  formatMoney,
  medianOf,
  rateTone,
  RATE_TONE_HINT,
  type AvailabilityRow,
  type BodyType,
  type CargoListItem,
  type CargoSort,
  type FeedScope,
  type FeedLayout,
  type RateHint,
} from 'shared';
import { api } from '../api';
import BodyTypePicker, { BodyTypeOne } from '../BodyTypePicker';
import CargoMap from '../CargoMap';
import CountryFlag from '../CountryFlag';
import Icon from '../Icon';
import Modal from '../Modal';
import CargoDetail from './CargoDetail';
import RegionPicker from '../RegionPicker';
import { copyText, haptic } from '../telegram';
import { toast } from '../toast';
import { cargoToText } from '../cargoText';
import { useConnectionState, useLiveRefresh, useRealtime } from '../realtime';

interface Props {
  onOpen: (id: string) => void;
  /** Правка заявки: в раскладке «список и карточка» её открывает карточка справа. */
  onEdit: (id: string) => void;
}

/**
 * Что показать в быстрых кнопках, пока человек не выбрал своё.
 *
 * Парк у всех разный, а кнопки одни: манипуляторщику рефрижератор в
 * первом ряду не нужен. Поэтому набор берётся из профиля, а это —
 * только стартовое предположение для тех, кто ещё не настраивал.
 */
const DEFAULT_QUICK: BodyType[] = ['MANIPULATOR', 'TENT', 'REF', 'TRAWL'];

/** Подпись переключателя — что сейчас показано, а не что будет дальше. */
const LAYOUT_LABEL: Record<FeedLayout, string> = {
  CARDS: 'Карточки',
  RATE: 'Выгода',
  TABLE: 'Таблица',
  SPLIT: 'Список и карточка',
};

/** Сколько заявке осталось висеть в ленте: «ещё 6 ч», «< 1 ч», «2 дн». */
function expiresIn(iso: string): string | null {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'скоро снимется';
  if (h < 24) return `ещё ${h} ч`;
  return `ещё ${Math.floor(h / 24)} дн`;
}

export default function Feed({ onOpen, onEdit }: Props) {
  const [items, setItems] = useState<CargoListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  /**
   * Свёрнута ли панель поиска целиком.
   *
   * Направление, вес и сортировка занимают верх экрана постоянно, хотя
   * трогают их редко: задал условия — и дальше листаешь ленту. На
   * телефоне это половина первого экрана, на вебе — полоса над таблицей.
   * Свёрнутая панель показывает выбранное строкой и разворачивается
   * одним нажатием.
   *
   * При открытии свёрнута: человек приходит смотреть заявки, а не
   * фильтры. Развёрнутая по умолчанию, она съедала первый экран целиком,
   * и до первой заявки приходилось листать.
   */
  const [searchOpen, setSearchOpen] = useState(false);
  /**
   * Как искать. Не оформление, а три разные задачи: посмотреть всё,
   * подобрать под свой маршрут, посмотреть географию. Раньше «по пути»
   * лежало под «Ещё фильтры», где его не находили, а карта была кнопкой
   * в ряду действий.
   */
  const [mode, setMode] = useState<'feed' | 'route' | 'map'>('feed');
  /** Чем сужена лента правилами групп. Пусто — ограничений нет. */
  const [scope, setScope] = useState<FeedScope | null>(null);

  const [bodyType, setBodyType] = useState<BodyType | ''>('');
  /** Свои быстрые кнопки кузовов. Пусто — показываем набор по умолчанию. */
  const [favorites, setFavorites] = useState<BodyType[]>([]);
  /** Раскладка на широком экране: карточки читаются, таблица вмещает. */
  const [layout, setLayout] = useState<FeedLayout>('CARDS');
  /*
   * Выбранная заявка в раскладке «список и карточка».
   *
   * Здесь, а не в App: в остальных раскладках заявка открывается окном
   * поверх экрана, и владелец этого состояния — приложение. В сплите окна
   * нет, карточка живёт в правой колонке ленты, и выбор — её дело.
   */
  const [picked, setPicked] = useState<string | null>(null);
  const split = layout === 'SPLIT';

  const openCargo = (id: string) => {
    haptic();
    if (split) setPicked(id);
    else onOpen(id);
  };
  /**
   * Ориентир рынка над лентой.
   *
   * Медиана по закрытым сделкам уже считалась, но показывалась только
   * тому, кто публикует заявку. Водителю она нужнее: он решает, брать
   * или ждать, и без ориентира делает это вслепую.
   */
  const [hint, setHint] = useState<RateHint | null>(null);
  const [tuneQuick, setTuneQuick] = useState(false);
  const quickTypes = favorites.length > 0 ? favorites : DEFAULT_QUICK;
  const [fromRegion, setFromRegion] = useState('');
  const [toRegion, setToRegion] = useState('');
  /*
   * Фильтр «ставка от» — в рублях учёта: лента смешивает валюты, и
   * сравнивать введённое число с тенге и долларами напрямую нельзя.
   * Имя с суффиксом, чтобы это было видно на месте, а не выяснялось
   * чтением серверного условия.
   */
  const [minPriceRub, setMinPriceRub] = useState('');
  const [minPricePerKm, setMinPricePerKm] = useState('');
  const [minWeightT, setMinWeightT] = useState('');
  const [maxWeightT, setMaxWeightT] = useState('');
  const [onlyDirect, setOnlyDirect] = useState(false);
  const [sort, setSort] = useState<CargoSort>('new');

  /*
   * Догруз.
   *
   * `partialOnly` — только заявки, готовые ехать попутно. `trip` — маршрут
   * водителя, взятый из его же объявления о свободном месте: координаты
   * руками никто вводить не станет, а без них «по пути» не посчитать.
   */
  const [partialOnly, setPartialOnly] = useState(false);
  const [trips, setTrips] = useState<AvailabilityRow[]>([]);
  const [tripId, setTripId] = useState('');

  const trip = trips.find((t) => t.id === tripId) ?? null;

  // На вкладке «По пути» маршрут выбирается сам, если он один: заставлять
  // выбирать из списка длиной в один пункт незачем.
  useEffect(() => {
    if (mode === 'route' && !tripId && trips.length > 0) setTripId(trips[0]!.id);
  }, [mode, tripId, trips]);

  async function load(reset = true) {
    setLoading(true);
    setError(null);
    try {
      const res = await api.feed({
        bodyType: bodyType || undefined,
        fromRegion: fromRegion || undefined,
        toRegion: toRegion || undefined,
        minPrice: minPriceRub || undefined,
        minPricePerKm: minPricePerKm || undefined,
        minWeightKg: minWeightT ? Math.round(Number(minWeightT) * 1000) : undefined,
        maxWeightKg: maxWeightT ? Math.round(Number(maxWeightT) * 1000) : undefined,
        directOnly: onlyDirect || undefined,
        partial: partialOnly || undefined,
        // Подбор по пути включается только вместе с выбранным рейсом:
        // без координат сервер этот фильтр всё равно игнорирует.
        ...(mode === 'route' && trip?.hasRoute
          ? {
              routeFromLat: trip.fromLat!,
              routeFromLng: trip.fromLng!,
              routeToLat: trip.toLat!,
              routeToLng: trip.toLng!,
              detourKm: trip.detourKm,
              freeKg: trip.freeKg ?? undefined,
              freeLengthM: trip.freeLengthM ?? undefined,
            }
          : {}),
        sort,
        cursor: reset ? undefined : (cursor ?? undefined),
        limit: 20,
      });
      setItems((prev) => (reset ? res.items : [...prev, ...res.items]));
      setCursor(res.nextCursor);
      setScope(res.scope);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bodyType,
    fromRegion,
    toRegion,
    minPriceRub,
    minPricePerKm,
    minWeightT,
    maxWeightT,
    onlyDirect,
    partialOnly,
    tripId,
    sort,
    mode,
  ]);

  // Быстрые кнопки кузовов живут в профиле, а не в браузере: иначе
  // набор теряется при входе с телефона после веба.
  useEffect(() => {
    api
      .me()
      .then((m) => {
        setFavorites(m.favoriteBodyTypes ?? []);
        setLayout(m.feedLayout ?? 'CARDS');
      })
      .catch(() => {});
  }, []);

  // Ориентир рынка под текущий отбор. Сервер честно молчит, если сделок
  // на этом направлении было меньше трёх — тогда блок просто не появится.
  useEffect(() => {
    if (!fromRegion) {
      setHint(null);
      return;
    }
    const t = setTimeout(() => {
      api
        .rateHint({
          fromRegion,
          toRegion: toRegion || undefined,
          bodyType: bodyType || undefined,
        })
        .then(setHint)
        .catch(() => setHint(null));
    }, 300);
    return () => clearTimeout(t);
  }, [fromRegion, toRegion, bodyType]);

  // Свои объявления о свободном месте — источник маршрута для догруза.
  // Грузим один раз: их единицы, и меняются они реже, чем лента.
  useEffect(() => {
    api
      .availability({ mine: 'true' })
      .then((r) => setTrips(r.items.filter((t) => t.hasRoute)))
      .catch(() => {});
  }, []);

  // Сокет мог промолчать: вкладка спала, событие родилось в обход API.
  // Перечитываем ленту при возврате фокуса и раз в минуту.
  useLiveRefresh(() => void load(true));

  /*
   * Ориентир рынка — медиана по тому, что человек сейчас видит.
   *
   * Поставил фильтр «рефрижератор» — сравнение идёт с рефрижераторами.
   * Общая средняя по бирже смешала бы газели с тралами и красила бы
   * половину ленты в красный без всякого основания.
   */
  // Медиана рынка — в рублях: лента смешивает валюты, и сравнивать их
  // между собой можно только через общую базу.
  const marketPerKm = medianOf(items.map((c) => c.pricePerKmRub ?? 0));

  // `useLiveRefresh` перечитывает ленту раз в минуту и на каждом возврате
  // фокуса — тихо, поверх уже показанных карточек. `loading` при этом всё
  // равно включается (иначе не понять, когда запрос закончился), и без
  // этого отдельного признака счётчик «Найдено» и строка «Загрузка…»
  // мигали бы при каждом таком обновлении, хотя список не менялся.
  const initialLoad = loading && items.length === 0;

  const connection = useConnectionState();
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());

  /**
   * Совпадает ли пришедший груз с текущими фильтрами. Дублирует условия
   * серверного запроса: событие приходит всем подряд, а вставлять в список
   * нужно только подходящее — иначе фильтры на глазах перестают работать.
   */
  function matchesFilters(c: CargoListItem): boolean {
    if (bodyType && !c.bodyTypes.includes(bodyType)) return false;
    if (fromRegion && c.fromRegion !== fromRegion) return false;
    if (toRegion && c.toRegion !== toRegion) return false;
    if (minPriceRub && c.priceRub < Number(minPriceRub)) return false;
    // Фильтр «ставка от» задаётся в рублях — сравниваем с рублёвым.
    if (minPricePerKm && (c.pricePerKmRub ?? 0) < Number(minPricePerKm)) return false;
    if (minWeightT && c.weightKg < Number(minWeightT) * 1000) return false;
    if (maxWeightT && c.weightKg > Number(maxWeightT) * 1000) return false;
    if (onlyDirect && !c.author.isDirectShipper) return false;
    return true;
  }

  useRealtime(
    'cargo:new',
    (e) => {
      if (!matchesFilters(e.cargo)) return;

      let known = false;
      setItems((prev) => {
        const at = prev.findIndex((c) => c.id === e.cargo.id);
        if (at >= 0) {
          /*
           * Заявка уже в списке — заменяем её на месте, а не пропускаем.
           *
           * Тем же событием приходит новая лучшая ставка аукциона: раньше
           * его молча отбрасывали как дубль, и в ленте висела цена,
           * перебитая полчаса назад. Место в списке не трогаем — прыжок
           * карточки под пальцем хуже устаревшей цифры.
           */
          known = true;
          const next = [...prev];
          next[at] = e.cargo;
          return next;
        }
        // Наверх независимо от сортировки: при «сначала новые» это и есть
        // правильное место, при остальных — заметный сигнал, что груз появился.
        return [e.cargo, ...prev];
      });

      // Подсветка и отклик — только настоящей новинке: мигать на каждой
      // чужой ставке значит приучить не замечать подсветку вовсе.
      if (known) return;
      setFreshIds((prev) => new Set(prev).add(e.cargo.id));
      haptic('light');
    },
    [bodyType, fromRegion, toRegion, minPriceRub, minPricePerKm, onlyDirect],
  );

  useRealtime('cargo:closed', (e) => {
    setItems((prev) => prev.filter((c) => c.id !== e.cargoId));
  });

  const activeFilters = [
    bodyType,
    fromRegion,
    toRegion,
    minPriceRub,
    minPricePerKm,
    minWeightT,
    maxWeightT,
    onlyDirect ? '1' : '',
  ].filter(Boolean).length;

  /** Применённые условия для показа отдельными чипами. */
  function chipsOfActive() {
    const out: { key: string; label: string; clear: () => void }[] = [];
    if (fromRegion) out.push({ key: 'from', label: `из ${fromRegion}`, clear: () => setFromRegion('') });
    if (toRegion) out.push({ key: 'to', label: `в ${toRegion}`, clear: () => setToRegion('') });
    if (bodyType) out.push({ key: 'body', label: BODY_TYPE_LABEL[bodyType], clear: () => setBodyType('') });
    if (minPriceRub) out.push({ key: 'price', label: `от ${minPriceRub} ₽`, clear: () => setMinPriceRub('') });
    if (minPricePerKm) out.push({ key: 'ppk', label: `от ${minPricePerKm} ₽/км`, clear: () => setMinPricePerKm('') });
    if (minWeightT || maxWeightT) {
      out.push({
        key: 'weight',
        label: `${minWeightT || '0'}–${maxWeightT || '∞'} т`,
        clear: () => { setMinWeightT(''); setMaxWeightT(''); },
      });
    }
    if (onlyDirect) out.push({ key: 'direct', label: 'прямые', clear: () => setOnlyDirect(false) });
    return out;
  }

  function reset() {
    setBodyType('');
    setFromRegion('');
    setToRegion('');
    setMinPriceRub('');
    setMinPricePerKm('');
    setMinWeightT('');
    setMaxWeightT('');
    setOnlyDirect(false);
  }

  const body = (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ margin: 0 }}>Грузы</h2>
          <div className="meta">
            {initialLoad ? 'Ищу…' : `Найдено: ${items.length}${cursor ? '+' : ''}`}
          </div>
        </div>
        <span className={`live live--${connection}`}>
          {connection === 'online'
            ? 'в реальном времени'
            : connection === 'connecting'
              ? 'подключаюсь…'
              : 'нет связи'}
        </span>
      </div>

      {/* Строка поиска: направление и сортировка — то, что меняют чаще всего.
          Остальное убрано в панель, иначе первый экран занят настройками,
          а не грузами. */}
      {/* Три способа искать. Вкладками, а не настройкой: человек
          переключается между ними по нескольку раз в день, в зависимости
          от задачи, а не выбирает один раз навсегда. */}
      <div className="seg" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={mode === 'feed' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => { haptic(); setMode('feed'); }}
        >
          Лента
        </button>
        <button
          type="button"
          className={mode === 'route' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => { haptic(); setMode('route'); }}
        >
          По пути
        </button>
        <button
          type="button"
          className={mode === 'map' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => { haptic(); setMode('map'); }}
        >
          Карта
        </button>
      </div>

      <div className="searchbar">
        <button
          type="button"
          data-tour="feed-filters"
          className="searchbar__toggle"
          onClick={() => { haptic(); setSearchOpen((v) => !v); }}
          aria-expanded={searchOpen}
        >
          <Icon name="filter" size={15} />
          <span className="searchbar__summary">
            {/* Свёрнутая панель обязана показывать, что в ней задано, —
                иначе человек не поймёт, почему в ленте пусто. */}
            {fromRegion || 'Любой регион'} → {toRegion || 'любой'}
            {bodyType ? ` · ${BODY_TYPE_LABEL[bodyType]}` : ''}
            {activeFilters > 0 ? ` · фильтров ${activeFilters}` : ''}
          </span>
          <Icon name={searchOpen ? 'up' : 'down'} size={15} />
        </button>

        {searchOpen && (
        <div className="searchbar__grid">
          <div className="field">
            <label>Откуда</label>
            <RegionPicker
              label="Регион загрузки"
              value={fromRegion}
              onChange={setFromRegion}
              allowEmpty="Любой регион"
            />
          </div>

          {/* Обратный рейс ищут сразу после прямого — кнопка экономит
              два выбора из длинного списка. */}
          <button
            className="icon-btn swap"
            title="Поменять местами"
            aria-label="Поменять направление"
            onClick={() => {
              haptic();
              setFromRegion(toRegion);
              setToRegion(fromRegion);
            }}
          >
            <Icon name="swap" size={17} />
          </button>

          <div className="field">
            <label>Куда</label>
            <RegionPicker
              label="Регион выгрузки"
              value={toRegion}
              onChange={setToRegion}
              allowEmpty="Любой регион"
            />
          </div>


          <div className="field field--pair">
            <label>Вес, т</label>
            <div className="pair">
              <input
                type="number"
                inputMode="decimal"
                placeholder="от"
                value={minWeightT}
                onChange={(e) => setMinWeightT(e.target.value)}
              />
              <input
                type="number"
                inputMode="decimal"
                placeholder="до"
                value={maxWeightT}
                onChange={(e) => setMaxWeightT(e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label>Сортировка</label>
            <select value={sort} onChange={(e) => setSort(e.target.value as CargoSort)}>
              {CARGO_SORTS.map((s) => (
                <option key={s} value={s}>
                  {CARGO_SORT_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
        </div>

        )}

        {searchOpen && (
        <div className="searchbar__actions">
          <button
            className={activeFilters > 0 ? 'chip chip--on' : 'chip'}
            onClick={() => { haptic(); setShowFilters((v) => !v); }}
          >
            <Icon name="filter" size={15} />
            Ещё фильтры{activeFilters > 0 ? ` · ${activeFilters}` : ''}
          </button>

          <button className="chip chip--ghost" onClick={reset} disabled={activeFilters === 0}>
            Очистить
          </button>

          {/* Карточки и «выгода» доступны везде; таблица — только на
              широком экране, поэтому на телефоне круг из двух пунктов. */}
          <button
            className="chip"
            onClick={() => {
              haptic();
              // Таблице нужен широкий экран, сплиту — ещё шире: две
              // колонки на 900 px дают по 450, и в карточке не помещается
              // ни маршрут, ни форма отклика.
              const w = window.innerWidth;
              const ring: FeedLayout[] =
                w >= 1200
                  ? ['CARDS', 'RATE', 'TABLE', 'SPLIT']
                  : w >= 900
                    ? ['CARDS', 'RATE', 'TABLE']
                    : ['CARDS', 'RATE'];
              const at = ring.indexOf(layout);
              const next = ring[(at + 1) % ring.length]!;
              setLayout(next);
              void api.updateMe({ feedLayout: next }).catch(() => {});
            }}
          >
            {LAYOUT_LABEL[layout]}
          </button>
        </div>
        )}
      </div>

      {/*
        * Почему заявок меньше, чем на площадке.
        *
        * Правило группы со стороны неотличимо от поломки: фильтры стоят
        * на «любой регион», кнопка «Очистить» погашена, а в ленте одно
        * направление. Первое, что приходит в голову, — «за мной остался
        * чужой фильтр». Поэтому ограничение названо прямо, вместе
        * с группой, которая его задала.
        */}
      {scope && (
        <div className="notice" role="status">
          <Icon name="shield" size={15} />
          <div>
            Лента сужена правилами
            {scope.groups.length > 0
              ? ` группы «${scope.groups.join('», «')}»`
              : ' площадки'}
            .
            {scope.allowedFrom && (
              <> Отправление только из: {scope.allowedFrom.join(', ')}.</>
            )}
            {scope.allowedTo && <> Доставка только в: {scope.allowedTo.join(', ')}.</>}
            {scope.blockedFrom.length > 0 && (
              <> Скрыто отправление из: {scope.blockedFrom.join(', ')}.</>
            )}
            {scope.blockedTo.length > 0 && (
              <> Скрыта доставка в: {scope.blockedTo.join(', ')}.</>
            )}
            <div className="meta">
              Это настройка площадки, а не ваш фильтр — кнопка «Очистить» её
              не снимет. За изменением — к администратору.
            </div>
          </div>
        </div>
      )}

      {/* Быстрый выбор кузова: манипулятор ищут отдельно от обычных
          перевозок, и лезть ради этого в фильтры незачем. */}
      <div className="chips" style={{ marginBottom: 12 }}>
        <button
          className={`chip ${bodyType === '' ? 'chip--on' : ''}`}
          onClick={() => { haptic(); setBodyType(''); }}
        >
          Все кузова
        </button>
        {quickTypes.map((b) => (
          <button
            key={b}
            className={`chip ${bodyType === b ? 'chip--on' : ''}`}
            onClick={() => { haptic(); setBodyType(b); }}
          >
            {BODY_TYPE_LABEL[b]}
          </button>
        ))}
        <button
          className="chip chip--ghost"
          title="Выбрать, какие кузова держать в быстрых кнопках"
          onClick={() => { haptic(); setTuneQuick(true); }}
        >
          Настроить
        </button>
      </div>

      {/* Настройка быстрых кнопок. Выбор уходит в профиль сразу: экран
          «сохранить» ради четырёх галочек — лишний шаг. */}
      <Modal open={tuneQuick} onClose={() => setTuneQuick(false)} title="Быстрые кнопки">
        <div className="meta" style={{ marginBottom: 8 }}>
          Отметьте кузова, с которыми работаете. Они встанут в ряд над
          лентой — у всех свои, парк ведь разный.
        </div>
        <BodyTypePicker
          value={favorites}
          onChange={(v) => {
            setFavorites(v);
            void api.updateMe({ favoriteBodyTypes: v }).catch(() => {});
          }}
          label="Быстрые кнопки"
        />
      </Modal>

      <div className="chips" style={{ marginBottom: 12 }}>
        <button
          className={partialOnly ? 'chip chip--on' : 'chip'}
          onClick={() => {
            haptic();
            setPartialOnly((v) => !v);
          }}
          data-tip="Заявки, которые можно везти попутно, часть кузова"
        >
          Догруз
        </button>

        {/* Выбор рейса — только на своей вкладке: в общей ленте он был
            ещё одним непонятным пунктом среди фильтров. */}
        {mode === 'route' && trips.length > 1 && (
          <select
            className="chip-select"
            value={tripId}
            onChange={(e) => setTripId(e.target.value)}
            aria-label="Подобрать по маршруту"
          >
            {trips.map((t) => (
              <option key={t.id} value={t.id}>
                {t.fromRegion} → {t.toRegion ?? '—'}
              </option>
            ))}
          </select>
        )}
      </div>

      {mode === 'route' && trip && (
        <div className="meta" style={{ marginBottom: 12 }}>
          Показываю грузы в коридоре ±{trip.detourKm} км вдоль маршрута
          {trip.freeKg ? `, не тяжелее ${(trip.freeKg / 1000).toFixed(1)} т` : ''}
          {trip.freeLengthM ? `, не длиннее ${trip.freeLengthM} м` : ''}. Сначала
          те, у кого меньше крюк.
        </div>
      )}

      {/* Вкладка «По пути» без объявленной машины бессмысленна: коридор
          не от чего считать. Объясняем это, а не показываем пустую ленту. */}
      {mode === 'route' && trips.length === 0 && (
        <div className="empty" style={{ marginBottom: 12 }}>
          Чтобы подобрать груз по пути, объявите свободную машину в разделе
          «Машины» — маршрут возьмём оттуда.
        </div>
      )}

      {mode === 'map' && (
        <div style={{ marginBottom: 12 }}>
          <CargoMap
            query={{
              bodyType: bodyType || undefined,
              fromRegion: fromRegion || undefined,
              toRegion: toRegion || undefined,
              minPrice: minPriceRub || undefined,
              minWeightKg: minWeightT ? Math.round(Number(minWeightT) * 1000) : undefined,
              maxWeightKg: maxWeightT ? Math.round(Number(maxWeightT) * 1000) : undefined,
              directOnly: onlyDirect || undefined,
              partial: partialOnly || undefined,
            }}
            // Через ту же точку, что и список: в раскладке «список и
            // карточка» заявка с карты открывается в соседней колонке,
            // а не окном поверх самой карты.
            onOpen={openCargo}
          />
        </div>
      )}

      {/* Применённые условия видны списком: иначе человек забывает, почему
          лента пустая, и решает, что грузов нет. */}
      {activeFilters > 0 && (
        <div className="chips" style={{ marginBottom: 12 }}>
          {chipsOfActive().map((f) => (
            <button key={f.key} className="chip chip--applied" onClick={f.clear}>
              {f.label}
              <Icon name="close" size={13} />
            </button>
          ))}
          <button className="chip chip--ghost" onClick={reset}>
            Сбросить всё
          </button>
        </div>
      )}

      {showFilters && (
        <div className="card">
          <div className="field">
            <label>Тип кузова</label>
            <BodyTypeOne
              value={bodyType}
              onChange={setBodyType}
              anyLabel="Любой кузов"
              favorites={favorites}
              label="Тип кузова"
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label>Ставка от, ₽</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="0"
                value={minPriceRub}
                onChange={(e) => setMinPriceRub(e.target.value)}
              />
            </div>
            <div className="field">
              <label>₽/км от</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="0"
                value={minPricePerKm}
                onChange={(e) => setMinPricePerKm(e.target.value)}
              />
            </div>
          </div>

          <label className="check">
            <input
              type="checkbox"
              checked={onlyDirect}
              onChange={(e) => { haptic(); setOnlyDirect(e.target.checked); }}
            />
            Только прямые грузовладельцы
          </label>

          <div className="field-row" style={{ marginTop: 12 }}>
            <button className="secondary" onClick={() => setShowFilters(false)}>
              Свернуть
            </button>
            {activeFilters > 0 && (
              <button className="danger" onClick={reset}>
                Сбросить
              </button>
            )}
          </div>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {/* На широком экране лента раскладывается в две колонки —
          одна колонка карточек оставляет полмонитора пустым.
          На вкладке карты списка нет: карта и есть представление
          того же отбора, дублировать его снизу незачем. */}
      {/* Ориентир рынка. Не «дорого/дёшево вообще» — медиана по закрытым
          сделкам ровно на том отборе, который человек сейчас видит. */}
      {mode !== 'map' && hint?.available && hint.medianPerKm != null && (
        <div className="market">
          <div>
            <div className="market__label">Рынок сейчас</div>
            <div className="meta">
              {hint.basis === 'route' ? 'на этом направлении' : 'из этого региона'}
              {` · ${hint.samples} сделок`}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="market__value tabular">{hint.medianPerKm} ₽/км</div>
            {hint.lowPerKm != null && hint.highPerKm != null && (
              <div className="meta tabular">
                разброс {hint.lowPerKm}–{hint.highPerKm}
              </div>
            )}
          </div>
        </div>
      )}

      {mode !== 'map' && layout === 'TABLE' && (
        <div className="feed-table-wrap">
          <table className="feed-table">
            <thead>
              <tr>
                <th>Маршрут</th>
                <th>Кузов</th>
                <th className="num">Вес</th>
                <th className="num">Км</th>
                <th className="num">₽/км</th>
                <th className="num">Ставка</th>
                <th>Загрузка</th>
                <th>Заказчик</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  className={freshIds.has(c.id) ? 'is-fresh' : undefined}
                  tabIndex={0}
                  onClick={() => openCargo(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openCargo(c.id);
                    }
                  }}
                >
                  <td className="route-cell">
                    <CountryFlag code={c.fromCountry} address={c.fromAddress} />
                    {c.fromAddress} → <CountryFlag code={c.toCountry} address={c.toAddress} />
                    {c.toAddress}
                  </td>
                  <td className="muted">
                    {c.bodyTypes.map((b) => BODY_TYPE_LABEL[b]).join(', ')}
                  </td>
                  <td className="num muted tabular">{(c.weightKg / 1000).toFixed(1)} т</td>
                  <td className="num muted tabular">{c.distanceKm ?? '—'}</td>
                  <td
                    className={`num tabular strong price--${rateTone(c.pricePerKmRub, marketPerKm)}`}
                  >
                    {c.pricePerKmRub ?? '—'}
                  </td>
                  <td className="num tabular strong">{formatMoney(c.price, c.currency)}</td>
                  <td className="muted">
                    {new Date(c.loadDateFrom).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </td>
                  <td className="muted">{c.author.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {mode !== 'map' && layout === 'RATE' && (
        <div className="feed-grid">
          {items.map((c) => (
            <div
              key={c.id}
              className={`card card--tap rate-card ${freshIds.has(c.id) ? 'card--fresh' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openCargo(c.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCargo(c.id);
                }
              }}
            >
              <div className="rate-card__top">
                <div style={{ minWidth: 0 }}>
                  <div className="route">
                    <CountryFlag code={c.fromCountry} address={c.fromAddress} />
                    {c.fromAddress}
                    <Icon name="arrow" size={15} />
                    <CountryFlag code={c.toCountry} address={c.toAddress} />
                    {c.toAddress}
                  </div>
                  <div className="meta">
                    {c.distanceKm ? `${c.distanceKm} км` : 'расстояние не рассчитано'}
                    {deliveryEta(c.distanceKm) && ` · в пути ${deliveryEta(c.distanceKm)!.label}`}
                  </div>
                </div>
                <div className="rate-card__perkm">
                  <div
                    className={`rate-card__num tabular price--${rateTone(c.pricePerKmRub, marketPerKm)}`}
                  >
                    {c.pricePerKmRub ?? '—'}
                  </div>
                  <div className="meta">₽ за км</div>
                </div>
              </div>

              <div className="chips" style={{ marginTop: 'var(--s2)' }}>
                {c.bodyTypes.slice(0, 2).map((b) => (
                  <span key={b} className="badge">{BODY_TYPE_LABEL[b]}</span>
                ))}
                <span className="badge">{(c.weightKg / 1000).toFixed(1)} т</span>
                {c.author.isDirectShipper && (
                  <span className="badge badge--direct">прямой заказчик</span>
                )}
              </div>

              <div className="divider" />

              <div className="row">
                <div>
                  <div className="price tabular">{formatMoney(c.price, c.currency)}</div>
                  <div className="meta">
                    загрузка{' '}
                    {new Date(c.loadDateFrom).toLocaleDateString('ru-RU', {
                      day: 'numeric',
                      month: 'short',
                    })}
                    {expiresIn(c.expiresAt) && <> · {expiresIn(c.expiresAt)}</>}
                  </div>
                </div>
                <button
                  type="button"
                  className="secondary btn--compact"
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyText(cargoToText(c)).then((ok) =>
                      ok ? toast.success('Заявка скопирована') : toast.error('Не вышло'),
                    );
                  }}
                >
                  Копировать
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode !== 'map' && (layout === 'CARDS' || split) && (
      <>
      <div className="feed-grid">
      {items.map((c) => (
        <div
          key={c.id}
          className={`card card--tap cargo ${freshIds.has(c.id) ? 'card--fresh' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => openCargo(c.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openCargo(c.id);
            }
          }}
        >
          <div className="cargo__head">
            <div className="route">
              <CountryFlag code={c.fromCountry} address={c.fromAddress} />
              {c.fromAddress}
              <Icon name="arrow" size={15} />
              <CountryFlag code={c.toCountry} address={c.toAddress} />
              {c.toAddress}
            </div>
            {/* Закрытая публикация. Виден значок только тем, кому заявка
                и так доступна, — но автору он объясняет, почему откликов
                меньше, чем он ждал. */}
            {c.closed && (
              <span className="badge badge--closed" data-tip="Видна только группе">
                <Icon name="shield" size={12} />
                только группе
              </span>
            )}
            {/* Свой отклик — первое, что нужно знать в ленте: без отметки
                перевозчик открывает карточку, чтобы вспомнить, писал ли он
                сюда, и так по каждой второй. */}
            {/* Аукцион виден прямо в ленте: без метки перевозчик примет
                стартовую цену за предложение и удивится отказу. */}
            {c.biddingMode === 'AUCTION' && (
              <span className="badge badge--auction">торги</span>
            )}
            {c.responded && (
              <span className="badge badge--direct">вы откликнулись</span>
            )}
            {freshIds.has(c.id) && (
              <span className="badge badge--accent">только что</span>
            )}
          </div>

          {/* Факты рейса плиткой: взгляд ищет вес и кузов, а не читает
              строку целиком. */}
          <div className="facts">
            {c.partial && (
              <span className="fact fact--accent" data-tip="Можно взять попутно">
                <Icon name="swap" size={14} />
                догруз
                {c.lengthM ? ` · ${c.lengthM} м` : ''}
              </span>
            )}
            {c.detourKm !== null && (
              <span className="fact fact--accent" data-tip="Крюк от вашего маршрута">
                <Icon name="route" size={14} />+{c.detourKm} км
              </span>
            )}
            <span className="fact" data-tip="Вес груза">
              <Icon name="box" size={14} />
              {(c.weightKg / 1000).toFixed(1)} т
            </span>
            {/* Кузовов может быть девять — в карточку они не помещаются
                и раздвигают её. Показываем два, остальное числом; полный
                список в подсказке. */}
            <span
              className="fact"
              data-tip={
                c.bodyTypes.length > 2
                  ? `Подходит: ${c.bodyTypes.map((b) => BODY_TYPE_LABEL[b]).join(', ')}`
                  : 'Подходящий кузов'
              }
            >
              <Icon name="truck" size={14} />
              <span className="fact__t">
                {c.bodyTypes.slice(0, 2).map((b) => BODY_TYPE_LABEL[b]).join(', ')}
              </span>
              {c.bodyTypes.length > 2 && (
                <b className="fact__more">+{c.bodyTypes.length - 2}</b>
              )}
            </span>
            {c.distanceKm ? (
              <span className="fact" data-tip="Расстояние по маршруту">
                <Icon name="route" size={14} />
                {c.distanceKm} км
              </span>
            ) : null}
            {deliveryEta(c.distanceKm) ? (
              <span className="fact" data-tip="Ориентировочный срок доставки">
                <Icon name="clock" size={14} />
                {deliveryEta(c.distanceKm)!.label}
              </span>
            ) : null}
          </div>

          {c.bodyTypes.includes('MANIPULATOR') &&
            (c.craneCapacityKg || c.craneReachM) && (
              <div className="meta">
                Кран:
                {c.craneCapacityKg
                  ? ` до ${(c.craneCapacityKg / 1000).toFixed(1)} т`
                  : ''}
                {c.craneReachM ? ` · вылет ${c.craneReachM} м` : ''}
              </div>
            )}

          <div className="cargo__price">
            <div>
              {/* Цвет — относительно медианы текущей выборки. Абсолютных
                  «дорого» и «дёшево» на этом рынке не существует. */}
              <span
                className={
                  rateTone(c.pricePerKmRub, marketPerKm)
                    ? `price price--${rateTone(c.pricePerKmRub, marketPerKm)}`
                    : 'price'
                }
                data-tip={
                  rateTone(c.pricePerKmRub, marketPerKm)
                    ? RATE_TONE_HINT[rateTone(c.pricePerKmRub, marketPerKm)!]
                    : undefined
                }
              >
                {formatMoney(c.price, c.currency)}
              </span>
              {c.pricePerKmRub ? (
                <span className="per-km"> · {c.pricePerKmRub} ₽/км</span>
              ) : null}
            </div>
            <span className={c.offersCount > 0 ? 'badge badge--warn' : 'badge'}>
              {c.offersCount > 0 ? `${c.offersCount} откл.` : 'нет откликов'}
            </span>
          </div>

          <div className="cargo__foot">
            <span className="cargo__author">
              {c.author.name}
              {c.author.ratingCount > 0 ? (
                <span className="rating"> ★ {c.author.rating.toFixed(1)}</span>
              ) : (
                <span className="meta"> · без оценок</span>
              )}
              {c.author.cancelRate !== null && c.author.cancelRate > 20 && (
                <span className="warn-inline"> · отмен {c.author.cancelRate}%</span>
              )}
            </span>

            <span className="cargo__tags">
              {c.author.isDirectShipper && (
                <span className="badge badge--direct">прямой</span>
              )}
              {c.author.verified && (
                <span className="badge badge--verified">проверен</span>
              )}
              {c.delayDays > 0 && <span className="badge">отсрочка {c.delayDays} дн.</span>}
              <span className="badge">{ENTITY_TYPE_LABEL[c.author.entityType]}</span>
              {c.viewsCount > 0 && <span className="badge">{c.viewsCount} 👁</span>}
            </span>
          </div>
        </div>
      ))}
      </div>

      </>
      )}

      {mode !== 'map' && !initialLoad && items.length === 0 && (
        <div className="empty">
          Подходящих грузов нет.
          <br />
          Смягчите фильтры или подпишитесь на направление в профиле.
        </div>
      )}

      {mode !== 'map' && initialLoad && <div className="empty">Загрузка…</div>}

      {mode !== 'map' && cursor && !loading && (
        <button className="secondary" onClick={() => void load(false)}>
          Показать ещё
        </button>
      )}

    </>
  );

  if (!split) return body;

  /*
   * Список слева, карточка справа — без модального окна.
   *
   * Диспетчер за большим экраном просматривает подряд десятки заявок, и
   * каждая из них открывалась окном поверх ленты: открыл, прочитал,
   * закрыл, нашёл глазами, где остановился. Здесь лента никуда не
   * девается, а карточка сменяется в соседней колонке.
   */
  return (
    <div className="split">
      <div className="split__list">{body}</div>
      <div className="split__detail">
        {picked ? (
          <CargoDetail
            // key: без него переход к соседней заявке переиспользует
            // компонент вместе с уже набранной ставкой предыдущей.
            key={picked}
            id={picked}
            onDone={() => setPicked(null)}
            onOpen={(next) => setPicked(next)}
            onEdit={onEdit}
          />
        ) : (
          <div className="empty">Выберите заявку слева — она откроется здесь.</div>
        )}
      </div>
    </div>
  );
}
